/** Driver app — Moji dokumenti: view files, statuses, upload new versions. */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Eye, Upload, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";

const STATUS_UI = {
  valid: { emoji: "🟢", label: "Valjan", cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" },
  expiring: { emoji: "🟡", label: "Uskoro istječe", cls: "bg-amber-500/15 border-amber-500/40 text-amber-400" },
  expired: { emoji: "🔴", label: "Istekao", cls: "bg-red-500/15 border-red-500/40 text-red-400" },
  none: { emoji: "", label: "Bez roka", cls: "bg-white/5 border-white/15 text-zinc-500" },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("hr-HR") : "—");

export default function DriverDocuments() {
  const [files, setFiles] = useState([]);
  const [attention, setAttention] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const addRef = useRef(null);
  const replaceRefs = useRef({});

  const load = async () => {
    try {
      const { data } = await driverApi.get("/driver/documents");
      setFiles(data.files || []);
      setAttention(data.attention || 0);
    } catch {
      toast.error("Učitavanje dokumenata nije uspjelo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const view = async (f) => {
    try {
      const resp = await driverApi.get(`/driver/documents/${f.idx}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(resp.data), "_blank");
    } catch {
      toast.error("Otvaranje dokumenta nije uspjelo.");
    }
  };

  const upload = async (file, { slot, label } = {}) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (slot) fd.append("slot", slot);
      if (label) fd.append("label", label);
      await driverApi.post("/driver/documents/upload", fd);
      toast.success("Dokument učitan — ured će ga provjeriti.");
      setNewLabel("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Učitavanje nije uspjelo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 pb-16">
      <div className="max-w-xl mx-auto px-4 pt-6" data-testid="driver-documents-page">
        <Link to="/driver/dashboard" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition mb-4"
              data-testid="documents-back-link">
          <ArrowLeft className="w-4 h-4" /> Natrag na početnu
        </Link>
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-5 h-5 text-[#C5A059]" />
          <h1 className="text-xl font-light text-white">Moji dokumenti</h1>
        </div>
        <p className="text-xs text-zinc-500 mb-5">Vozačka dozvola, certifikati, ugovori i ostali dokumenti.</p>

        {attention > 0 && (
          <div className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 flex items-center gap-3 text-xs text-amber-200"
               data-testid="documents-attention-banner">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />
            {attention === 1 ? "1 dokument zahtijeva pažnju" : `${attention} dokumenta zahtijevaju pažnju`} — istječu uskoro ili su istekli. Učitajte nove verzije.
          </div>
        )}

        {loading ? (
          <p className="text-sm text-zinc-500">Učitavanje…</p>
        ) : files.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center" data-testid="documents-empty">
            <p className="text-zinc-400 text-sm">Još nema učitanih dokumenata.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {files.map((f) => {
              const st = STATUS_UI[f.status] || STATUS_UI.none;
              return (
                <div key={f.idx} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4" data-testid={`driver-doc-${f.idx}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white">{f.label}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        Učitano {fmtDate(f.uploaded_at)}
                        {f.expiry_date && <> · vrijedi do <span className="text-zinc-300">{fmtDate(f.expiry_date)}</span></>}
                        {f.signed_by && <span className="text-emerald-500/80"> · potpisano</span>}
                      </p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border whitespace-nowrap ${st.cls}`}
                          data-testid={`driver-doc-status-${f.idx}`}>
                      {st.emoji} {st.label}{f.status === "expiring" && f.days_left !== null ? ` · ${f.days_left}d` : ""}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => view(f)}
                            className="flex-1 h-9 rounded-full border border-white/15 text-xs text-zinc-300 hover:bg-white/10 transition inline-flex items-center justify-center gap-1.5"
                            data-testid={`driver-doc-view-${f.idx}`}>
                      <Eye className="w-3.5 h-3.5" /> Pregledaj
                    </button>
                    <button onClick={() => replaceRefs.current[f.idx]?.click()} disabled={busy}
                            className="flex-1 h-9 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/40 text-xs text-[#C5A059] hover:bg-[#C5A059]/25 transition inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                            data-testid={`driver-doc-upload-${f.idx}`}>
                      <Upload className="w-3.5 h-3.5" /> Nova verzija
                    </button>
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif" className="hidden"
                           ref={(el) => { replaceRefs.current[f.idx] = el; }}
                           onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], { slot: f.slot })} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-5 rounded-2xl border border-white/10 bg-[#0d0d0d] p-4">
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Dodaj novi dokument</p>
          <div className="flex gap-2">
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                   placeholder="Naziv (npr. Liječnička potvrda)"
                   className="flex-1 bg-black/40 border border-white/10 px-3 py-2 rounded-lg text-xs focus:outline-none focus:border-[#C5A059]/60"
                   data-testid="driver-doc-new-label" />
            <button disabled={busy}
                    onClick={() => newLabel.trim() ? addRef.current?.click() : toast.error("Unesite naziv dokumenta.")}
                    className="inline-flex items-center gap-1 px-4 rounded-full bg-[#C5A059] text-black text-xs font-semibold hover:bg-[#D4AF37] disabled:opacity-50 transition"
                    data-testid="driver-doc-add-btn">
              <Plus className="w-3.5 h-3.5" /> Dodaj
            </button>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif" className="hidden" ref={addRef}
                   onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], { label: newLabel.trim() })} />
          </div>
        </div>
      </div>
    </div>
  );
}
